// Phase 9 — Dashboard-first UI rebalance. AppShell — slim top bar layout.
//
// Top bar with: DailyDeck wordmark → Alerts bell (with count) → Settings gear.
// No sidebar nav links. The home page is the primary surface; modules are
// reached by clicking their card on the dashboard grid.

import { Bell, Settings } from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAlerts } from '../../hooks/useAlerts.js';
import { NavigationBar } from '../../components/ui/NavigationBar.js';
import { IconButton } from '../../components/ui/IconButton.js';
import { OfflineBanner } from '../../components/OfflineBanner.js';

export function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const { data: alerts } = useAlerts();
  const criticalCount = alerts?.filter((a) => a.priority === 'critical').length ?? 0;
  const totalCount = alerts?.length ?? 0;

  return (
    <div className="min-h-screen bg-surface-base text-text-primary">
      <OfflineBanner />
      <NavigationBar>
        <NavLink
          to="/"
          end
          className="text-title-3 font-semibold text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-default rounded-md px-2 py-1"
          aria-label="DailyDeck home"
        >
          DailyDeck
        </NavLink>

        <div className="ml-auto flex items-center gap-1">
          <IconButton
            aria-label={`Alerts${totalCount > 0 ? ` — ${totalCount} unread` : ''}`}
            onClick={() => navigate('/alerts')}
          >
            <Bell size={20} strokeWidth={1.5} aria-hidden="true" />
            {criticalCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-state-danger px-1 text-micro font-bold text-text-on-accent" aria-hidden="true">
                {criticalCount}
              </span>
            )}
          </IconButton>

          <IconButton
            aria-label="Settings"
            onClick={() => navigate('/settings')}
          >
            <Settings size={20} strokeWidth={1.5} aria-hidden="true" />
          </IconButton>
        </div>
      </NavigationBar>

      <main id="main" className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
